<template>
  <div
    :class="[
      $style.avatar,
      $style[`size_${size}`],
      {
        [$style[`appearance_${appearance}_${emphasis}`]]: color === undefined,
        [$style.square]: shape === 'square',
        [$style.hovered]: hovered,
      },
      { [$style.bordered]: bordered },
    ]"
    :style="{
      ...(color
        ? {
            '--color': useHoverColors ? color?.hoverBgColor : color?.bgColor,
            '--text-color': useHoverColors ? color?.hoverTextColor : color?.textColor,
          }
        : {}),
      ...(borderColor ? { '--border-color-override': borderColor } : {}),
      ...(borderWidth ? { '--border-width-override': borderWidth } : {}),
    }"
  >
    <slot>{{ displayedInitials }}</slot>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { avatarColorPalette } from "./avatarColors.ts";
import type { SCAAvatarProps } from "./types";

const props = withDefaults(defineProps<SCAAvatarProps>(), {
  initials: undefined,
  size: "medium",
  shape: "rounded",
  appearance: "blue",
  emphasis: "high",
  bordered: false,
  colorIdx: undefined,
  hovered: false,
  useHoverColors: false,
  borderColor: undefined,
  borderWidth: undefined,
});

const color = computed(() => (props.colorIdx !== undefined ? avatarColorPalette[props.colorIdx] : undefined));

const displayedInitials = computed(() => {
  if (!props.initials) return "";
  return props.size === "x3small" ? props.initials.charAt(0) : props.initials;
});
</script>

<style module lang="scss">
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../styles/helpers/appearance.scss" as AppearanceMixins;

.avatar {
  width: var(--avatar-size);
  height: var(--avatar-size);
  overflow: hidden;
  border-radius: var(--border-radius-full);
  border-color: var(--color-border-neutral-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  flex-grow: 0;
  --border-size: 2px;
  user-select: none;
  transition:
    background-color 0.3s ease-in-out,
    color 0.3s ease-in-out;

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &.bordered {
    border-width: var(--border-width-override, var(--border-size));
    border-style: solid;
    border-color: var(--border-color-override, var(--color-border-neutral-subtle));
    transition:
      border-color 0.25s ease-in-out,
      border-width 0.25s ease-in-out;
  }

  &.size_xlarge {
    --avatar-size: 100px;
    @include FigmaTypoMixins.typo-header-4xlarge-light;

    &.bordered:hover {
      border-color: var(--color-action-outline-hover);
    }
  }

  &.size_large {
    --avatar-size: 48px;
    --border-size: 1.5px;
    @include FigmaTypoMixins.typo-body-large;
  }

  &.size_medium {
    --avatar-size: 40px;
    @include FigmaTypoMixins.typo-body-medium;
  }

  &.size_small {
    --avatar-size: 28px;
    --border-size: 1px;
    @include FigmaTypoMixins.typo-header-small;
    line-height: 17.5px;
    letter-spacing: 0.24px;
  }

  &.size_xsmall {
    --avatar-size: 24px;
    --border-size: 1px;
    @include FigmaTypoMixins.typo-body-strong-xsmall;
  }

  &.size_x2small {
    --avatar-size: 20px;
    --border-size: 1px;
    @include FigmaTypoMixins.typo-body-strong-xsmall;
  }

  &.size_x3small {
    --avatar-size: 16px;
    --border-size: 0.5px;
    @include FigmaTypoMixins.typo-body-strong-xsmall;
  }

  &.size_80 {
    --avatar-size: 80px;
    @include FigmaTypoMixins.typo-body-large;
    font-size: 28px;

    &.bordered:hover,
    &.bordered.hovered {
      border-color: var(--color-action-outline-hover);
    }
  }

  &.square {
    border-radius: var(--border-radius-large);

    &.size_xsmall,
    &.size_x2small {
      border-radius: var(--border-radius-medium);
    }

    &.bordered {
      border: 1px solid var(--color-border-neutral-subtle);
    }
  }

  @include AppearanceMixins.appearanceColors;
  background-color: var(--color);
  color: var(--text-color);
}
</style>
